/**
 * The account statement in PDF and Excel (2.10, 19/10/2026): for internal use
 * and the accountant, one subject at a time, without the ledgers; customer and
 * supplier together when the subject is both. The same rules filter and sort
 * the invoices on the page, in the printed page and in the Excel of the API.
 */
import { z } from 'zod';
import { sortRows } from './column-sort.js';
import { LEDGER_CYCLES } from './ledgers.js';
import { fromCents, toCents } from './money.js';
import { statementTotals, } from './statements.js';
import { canWriteOff, CLOSURE_REASON_LABELS, invoiceStateLabel } from './treasury.js';
/** The filter on the state of the invoices; «small»: a residual up to the abbuono (1.81). */
export const STATEMENT_STATE_FILTERS = [
    'all',
    'open',
    'scaduta',
    'parziale',
    'saldata',
    'small',
];
/** How the filter reads in the heading of an export: «Fatture: da incassare». */
export function statementStateFilterLabel(filter, cycles) {
    const only = cycles.length === 1 ? cycles[0] : null;
    switch (filter) {
        case 'all':
            return 'tutte';
        case 'open':
            return only === 'attivo' ? 'da incassare' : only === 'passivo' ? 'da pagare' : 'aperte';
        case 'scaduta':
            return 'scadute';
        case 'parziale':
            return 'parziali';
        case 'saldata':
            return only === 'attivo' ? 'incassate' : only === 'passivo' ? 'pagate' : 'chiuse';
        case 'small':
            return 'residuo fino a 1,00 €';
    }
}
export function filterStatementInvoices(invoices, filter) {
    return invoices.filter((item) => filter === 'all'
        ? true
        : filter === 'open'
            ? item.state !== 'saldata'
            : filter === 'small'
                ? canWriteOff(item.residual)
                : item.state === filter);
}
/** The sortable columns of the invoices of a statement and what each sorts by. */
export const STATEMENT_INVOICE_SORTS = {
    number: (item) => item.date,
    due: (item) => item.nextDue,
    netDue: (item) => Number(item.netDue),
    paid: (item) => (toCents(item.netDue) - toCents(item.residual)) / 100,
    residual: (item) => Number(item.residual),
    state: (item) => item.state,
};
const SORT_KEYS = Object.keys(STATEMENT_INVOICE_SORTS);
export function sortStatementInvoices(invoices, sort) {
    const value = sort && sort.key in STATEMENT_INVOICE_SORTS
        ? STATEMENT_INVOICE_SORTS[sort.key]
        : undefined;
    return sortRows(invoices, sort, value);
}
/** «Scaduta da 12 giorni», «Incassata», «Sostituita»: the state as the pill on the page says it. */
export function statementInvoiceStateLabel(item) {
    if (item.cancelled)
        return 'Annullata';
    if (item.replaced)
        return 'Sostituita';
    if (item.kind !== 'nota_credito' && item.state === 'scaduta' && item.overdueDays > 0) {
        return `Scaduta da ${item.overdueDays} ${item.overdueDays === 1 ? 'giorno' : 'giorni'}`;
    }
    return invoiceStateLabel(item.state, item.direction, item.kind);
}
/** How a collection or payment was made, after its date and amount: «BCC», «abbuono»… */
export function statementPaymentLabel(payment, invoice, bankName) {
    if (payment.creditLink !== null) {
        return `${invoice.kind === 'nota_credito' ? 'sulla fattura' : 'nota di credito'} ${payment.creditLink}`;
    }
    if (payment.compensation !== null)
        return `compensata con la fattura ${payment.compensation}`;
    if (payment.closure) {
        return `senza movimento, ${CLOSURE_REASON_LABELS[payment.closure].toLowerCase()}`;
    }
    if (payment.writeOff)
        return 'abbuono';
    if (payment.movement)
        return bankName(payment.movement.accountCode);
    if (payment.card)
        return `Carta •••• ${payment.card}`;
    return 'a mano';
}
export function statementExport(statement, options) {
    const sides = statement.sides
        .filter((side) => options.cycles.includes(side.cycle))
        .map((side) => {
        const invoices = sortStatementInvoices(filterStatementInvoices(side.invoices, options.state), options.sorts[side.cycle] ?? null);
        return {
            cycle: side.cycle,
            opening: side.opening,
            totals: side.totals,
            invoices,
            shownTotals: statementTotals(invoices),
        };
    });
    const customer = sides.find((side) => side.cycle === 'attivo');
    const supplier = sides.find((side) => side.cycle === 'passivo');
    const net = customer && supplier
        ? {
            credit: customer.totals.residual,
            debit: supplier.totals.residual,
            net: fromCents(toCents(customer.totals.residual) - toCents(supplier.totals.residual)),
            overdueCredit: customer.totals.overdue,
            overdueDebit: supplier.totals.overdue,
        }
        : null;
    return {
        subject: statement.subject,
        state: options.state,
        sides,
        net,
        financial: statement.financial,
    };
}
/** «Scheda contabile - Demo S.r.l. - 2026-10-19»: without the characters a file name cannot have. */
export function statementExportFileName(subjectName, date) {
    const name = subjectName
        .replace(/[\\/:*?"<>|]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    return `Scheda contabile - ${name} - ${date}`;
}
const sortParamSchema = z
    .string()
    .regex(new RegExp(`^(${SORT_KEYS.join('|')}):(1|-1)$`))
    .transform((value) => {
    const [key, direction] = value.split(':');
    return { key: key, direction: direction === '-1' ? -1 : 1 };
});
/** What an export takes: the sides, the filter and the sort of each side. */
export const statementExportQuerySchema = z
    .object({
    cycles: z
        .string()
        .default(LEDGER_CYCLES.join(','))
        .transform((value) => value.split(',').filter(Boolean))
        .pipe(z.array(z.enum(LEDGER_CYCLES)).min(1)),
    state: z.enum(STATEMENT_STATE_FILTERS).default('all'),
    sortAttivo: sortParamSchema.optional(),
    sortPassivo: sortParamSchema.optional(),
})
    .transform((query) => ({
    cycles: query.cycles,
    state: query.state,
    sorts: { attivo: query.sortAttivo ?? null, passivo: query.sortPassivo ?? null },
}));
/** The query string of an export, for the Excel of the API and the printed page. */
export function statementExportSearch(options) {
    const params = new URLSearchParams({ cycles: options.cycles.join(','), state: options.state });
    for (const cycle of LEDGER_CYCLES) {
        const sort = options.sorts[cycle];
        if (sort && sort.key in STATEMENT_INVOICE_SORTS) {
            params.set(cycle === 'attivo' ? 'sortAttivo' : 'sortPassivo', `${sort.key}:${sort.direction}`);
        }
    }
    return params.toString();
}
export const STATEMENT_EXPORT_FORMATS = ['pdf', 'excel'];
/** The printed page tells the API it was opened, for the activity log. */
export const statementPrintedSchema = z.object({
    cycles: z.array(z.enum(LEDGER_CYCLES)).min(1),
    state: z.enum(STATEMENT_STATE_FILTERS),
});
//# sourceMappingURL=statement-export.js.map